import { ItemPropertyObjectValue } from "lib/types/itemPropertiesDialog";
import { regexPatterns } from "lib/utils/validations";

export const formatExpirationDate = (value: string) => {
  return value
    .replace(
      /[^0-9]/g,
      "", // To allow only numbers
    )
    .replace(
      /^([2-9])$/g,
      "0$1", // To handle 3 > 03
    )
    .replace(
      /^(1{1})([3-9]{1})$/g,
      "0$1/$2", // 13 > 01/3
    )
    .replace(
      /^0{1,}/g,
      "0", // To handle 00 > 0
    )
    .replace(
      /^([0-1]{1}[0-9]{1})([0-9]{1,2}).*/g,
      "$1/$2", // To handle 113 > 11/3
    );
};

export const currencyFormatter = (
  price: number | string = 0,
  showCurrencySymbol = true,
  options = { locale: "en-IN", currency: "INR" },
) => {
  const formatter = new Intl.NumberFormat(options.locale, {
    style: "currency",
    currency: options.currency,
  });
  const formattedPrice = formatter.format(+price);

  const finalPrice = Number.isInteger(Number(price))
    ? formattedPrice.slice(0, formattedPrice.length - 3)
    : formattedPrice;

  return showCurrencySymbol ? finalPrice : finalPrice.slice(1);
};

/**
 * formats a string input to a valid zip code
 *
 * @param  {string} value       The value to be formatted
 * @param  {object} values      The collection of values to check the country code from
 * @return {string}             Returns the formatted value
 */
export const formatZip = (value: string, values: any): string => {
  if (values?.country_code !== "IN") {
    return value.replace(regexPatterns.alphaNumericWithSpace, "");
  }
  if (regexPatterns.numeric.test(value)) {
    return value.replace(regexPatterns.numeric, "");
  }
  return value;
};

/** Formats a datetime value as "DD MMM YYYY, hh:mm AM/PM", matching the en-IN convention */
export const formatDateTimeValue = (value: ItemPropertyObjectValue): string | null => {
  if (typeof value !== "string" && typeof value !== "number") return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${datePart}, ${timePart}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const LATITUDE_RANGE: [number, number] = [-90, 90];
const LONGITUDE_RANGE: [number, number] = [-180, 180];

const toCoordinate = (raw: unknown): number | null => {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && raw.trim() !== "") return Number(raw);
  return null;
};

/** Reads the `lat`/`lng` keys off a location value into validated coordinates, or null if it can't be located. */
export const parseLocationValue = (value: ItemPropertyObjectValue): { lat: number; lng: number } | null => {
  if (!isRecord(value)) return null;

  const lat = toCoordinate(value.lat);
  const lng = toCoordinate(value.lng);
  if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < LATITUDE_RANGE[0] || lat > LATITUDE_RANGE[1]) return null;
  if (lng < LONGITUDE_RANGE[0] || lng > LONGITUDE_RANGE[1]) return null;

  return { lat, lng };
};
